import { Link } from '@tanstack/react-router'
import { clsx } from 'clsx'
import { LeagueFormat, type StandingsEntry, type League } from '../types'
import { teamSquadLink } from '../teamSquadLink'
import styles from './StandingsTable.module.css'

type StandingsTableProps = {
  league: League
  standings: StandingsEntry[]
}

function RankBadge({ rank }: { rank: number }) {
  return (
    <span className={clsx(
      styles.rankBadge,
      rank === 1 && styles.rankBadgeGold,
      rank === 2 && styles.rankBadgeSilver,
      rank === 3 && styles.rankBadgeBronze,
    )}>
      {rank}
    </span>
  )
}

function RankDelta({ current, previous }: { current: number; previous: number | null }) {
  // `previous` is null on a league's first scored GW (handled at the fetch boundary); the
  // isFinite check is a final guard so a missing/NaN value never renders "NaN posições".
  if (previous == null) return null
  const delta = previous - current  // positive = moved up
  if (!Number.isFinite(delta)) return null
  if (delta === 0) return <span className={styles.deltaNeutral}>— sem mudança</span>
  const noun = Math.abs(delta) === 1 ? 'posição' : 'posições'
  if (delta > 0) return <span className={styles.deltaUp}>▲ +{delta} {noun}</span>
  return <span className={styles.deltaDown}>▼ {delta} {noun}</span>
}

export function StandingsTable({ league, standings }: StandingsTableProps) {
  const isH2H = league.format === LeagueFormat.HeadToHead

  return (
    <div className={styles.wrapper}>
      <table className={styles.table}>
        <thead>
          <tr className={styles.head}>
            <th className={clsx(styles.cell, styles.colRank)} scope="col">#</th>
            <th className={clsx(styles.cell, styles.colTeam)} scope="col">Time</th>
            {isH2H && <th className={clsx(styles.cell, styles.colNum)} scope="col">H2H</th>}
            <th className={clsx(styles.cell, styles.colNum)} scope="col">
              <span className={styles.labelFull}>Pts Rodada</span>
              <span className={styles.labelShort}>Rod</span>
            </th>
            <th className={clsx(styles.cell, styles.colNum)} scope="col">
              <span className={styles.labelFull}>Pts Total</span>
              <span className={styles.labelShort}>Tot</span>
            </th>
            <th className={clsx(styles.cell, styles.colNum)} scope="col">
              <span className={styles.labelFull}>Transf.</span>
              <span className={styles.labelShort}>Tr</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {standings.map((entry) => {
            const linkParams = entry.isAverageParticipant ? null : teamSquadLink(entry.userId, league)
            return (
              <tr
                key={entry.userId}
                className={clsx(
                  styles.row,
                  entry.isCurrentUser && styles.isCurrentUser,
                  entry.isAverageParticipant && styles.isAverage,
                )}
              >
                <td className={clsx(styles.cell, styles.colRank)}>
                  <div className={styles.colRankInner}>
                    <RankBadge rank={entry.rank} />
                  </div>
                </td>
                <td className={clsx(styles.cell, styles.colTeam)}>
                  <div className={styles.teamCellInner}>
                    {linkParams ? (
                      <Link
                        to="/league/$leagueId/team/$userId"
                        params={{ leagueId: league.id, userId: linkParams.userId }}
                        search={{ gw: linkParams.gw }}
                        className={styles.teamLink}
                      >
                        {entry.teamName}
                        {!entry.hasSquad && (
                          <span className={styles.noSquadBadge}>Sem escalação</span>
                        )}
                      </Link>
                    ) : (
                      <span className={clsx(styles.teamName, entry.isAverageParticipant && styles.averageName)}>
                        {entry.teamName}
                        {!entry.hasSquad && !entry.isAverageParticipant && (
                          <span className={styles.noSquadBadge}>Sem escalação</span>
                        )}
                      </span>
                    )}
                    {!entry.isAverageParticipant && (
                      <RankDelta current={entry.rank} previous={entry.previousRank} />
                    )}
                  </div>
                </td>
                {isH2H && (
                  <td className={clsx(styles.cell, styles.colNum)}>
                    {entry.isAverageParticipant ? '—' : (entry.h2hPoints ?? 0)}
                  </td>
                )}
                <td className={clsx(styles.cell, styles.colNum)}>{entry.gwPoints}</td>
                <td className={clsx(styles.cell, styles.colNum)}>{entry.totalPoints}</td>
                <td className={clsx(styles.cell, styles.colNum)}>
                  {entry.isAverageParticipant ? '—' : entry.transfersUsed}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
